import { useNavigate } from "@tanstack/react-router";
import { useSnackbar } from "notistack";
import * as React from "react";
import { Spinner } from "components/elements";
import { useCurrentUser } from "hooks/useCurrentUser";
import { trpc } from "lib/trpc";
import { ClientPost, PostUpdate } from "@compass/shared/types";
import { DeletePostModal } from "./DeletePostModal";
import { SocialPost } from "./SocialPost";

type SocialPostProps = {
    post: ClientPost;
    isProfile?: boolean;
    isModalView?: boolean;
    compact?: boolean;
    target?: string;
};
export const SocialPostWrapper = ({
    post,
    isProfile,
    isModalView,
    compact,
    target,
}: SocialPostProps) => {
    const queryClient = trpc.useContext();
    const { enqueueSnackbar } = useSnackbar();
    const currentUser = useCurrentUser();
    const navigate = useNavigate();
    const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);

    const deletePostMutation = trpc.social.posts.delete.useMutation();
    const updatePostMutate = trpc.social.posts.update.useMutation();

    const handleUpdatePost = (params: PostUpdate, callback: () => void) => {
        updatePostMutate.mutate(params, {
            onSuccess: () => {
                callback();
                queryClient.social.posts.latest.invalidate();
                queryClient.social.posts.get.invalidate({ postId: post.id });
                queryClient.feed.unified.invalidate();
                queryClient.users.getPublic.invalidate();
                enqueueSnackbar("Post updated successfully.", { variant: "success" });
            },
            onError: (e) => {
                console.error(e);
                enqueueSnackbar(`${e}`, { variant: "error" });
            },
        });
    };

    const handleDeletePost = () => {
        if (deletePostMutation.isPending) return;
        deletePostMutation.mutate(
            { postId: post.id },
            {
                onSuccess: () => {
                    queryClient.social.posts.latest.invalidate();
                    queryClient.feed.unified.invalidate();
                    queryClient.notifications.latest.invalidate();
                    queryClient.users.getPublic.invalidate();
                    enqueueSnackbar("Post removed successfully.", { variant: "success" });
                    if (!isProfile) {
                        navigate({ to: "/social" });
                    }
                },
            },
        );
    };

    return (
        <>
            {deletePostMutation.isPending && (
                <div className="z-100 absolute left-0 top-0 flex h-full w-full items-center justify-center bg-slate-50/50">
                    <Spinner className="h-10 w-10 text-slate-600" />
                </div>
            )}
            <SocialPost
                post={post}
                isLoading={updatePostMutate.isPending}
                handleUpdatePost={handleUpdatePost}
                handleDeletePost={() => setIsDeleteModalOpen(true)}
                currentUser={currentUser}
                isModalView={isModalView}
                compact={compact}
                target={target}
            />
            <DeletePostModal
                isOpen={isDeleteModalOpen}
                setIsOpen={setIsDeleteModalOpen}
                onDelete={handleDeletePost}
                postTitle={post.title}
            />
        </>
    );
};
